import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Croissant, CheckCircle2, XCircle, CreditCard, Loader2, ExternalLink } from "lucide-react";
import { inr, fmtDate, statusColor } from "@/lib/utils-format";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicQuotePage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/quotes/${token}`);
      setData(data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Not found" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Handle Stripe redirect
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        setPaymentStatus(data);
        if (data.payment_status === "paid") {
          toast.success("Payment received. Thank you!");
          params.delete("session_id"); params.delete("paid"); setParams(params, { replace: true });
          load();
          return;
        }
      } catch (_) {}
      if (attempts < 8) setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const doAction = async (action) => {
    try {
      await axios.post(`${API}/public/quotes/${token}/${action}`);
      toast.success(action === "accept" ? "Quotation accepted!" : "Quotation rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const payNow = async () => {
    setPayLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/quotes/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setPayLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#8B7355]">Loading…</div>;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center text-red-600">{data.error}</div>;

  const { quote, company } = data;
  const remaining = (quote.grand_total || 0) - (quote.amount_paid || 0);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#EAE5D9] pb-4">
          <div className="flex items-center gap-3">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company.company_name} className="w-12 h-12 object-contain" />
            ) : (
              <div className="w-12 h-12 rounded-md bg-[#C05621] flex items-center justify-center">
                <Croissant className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <div className="font-[Outfit] font-semibold text-lg">{company?.company_name || "Bakemill Foods Private Limited"}</div>
              <div className="text-xs text-[#8B7355]">{[company?.city, company?.state].filter(Boolean).join(", ")}</div>
            </div>
          </div>
          <div className="text-right text-xs text-[#8B7355]">
            {company?.gstin && <div>GSTIN: {company.gstin}</div>}
            {company?.phone && <div>{company.phone}</div>}
            {company?.email && <div>{company.email}</div>}
          </div>
        </div>

        {/* Title */}
        <div className="flex items-start justify-between">
          <div>
            <div className="uc-label">{quote.doc_type === "invoice" ? "Invoice" : "Quotation"}</div>
            <h1 className="font-[Outfit] text-3xl font-semibold mt-1">{quote.number}</h1>
          </div>
          <div className="text-right space-y-1">
            <Badge variant="outline" className={`capitalize ${statusColor(quote.status)}`}>{quote.status}</Badge>
            {quote.payment_status && <div><Badge variant="outline" className={`capitalize ${statusColor(quote.payment_status)}`}>{quote.payment_status}</Badge></div>}
            <div className="text-xs text-[#8B7355]">{fmtDate(quote.created_at)}</div>
          </div>
        </div>

        {/* Public action banner */}
        {quote.public_action === "accepted" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3 flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="w-4 h-4" /> You accepted this quotation. We'll be in touch shortly.
          </div>
        )}
        {quote.public_action === "rejected" && (
          <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-center gap-2 text-red-800">
            <XCircle className="w-4 h-4" /> Quotation rejected.
          </div>
        )}
        {paymentStatus?.payment_status === "paid" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3 flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="w-4 h-4" /> Payment received. Thank you!
          </div>
        )}

        {/* Bill to */}
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5">
          <div className="uc-label mb-1">Bill To</div>
          <div className="font-semibold">{quote.customer?.name}</div>
          <div className="text-[#5C4033] text-sm">{quote.customer?.company}</div>
          <div className="text-[#5C4033] text-sm">{quote.customer?.email} · {quote.customer?.phone}</div>
        </div>

        {/* Items */}
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F2E8] uc-label">
              <tr>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Price</th>
                <th className="text-right px-4 py-3">GST</th>
                <th className="text-right px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.items?.map((it, i) => {
                const gross = it.quantity * it.unit_price;
                const net = gross - gross * ((it.discount_pct || 0) / 100);
                const gst = net * (it.gst_rate / 100);
                return (
                  <tr key={i} className="border-t border-[#EAE5D9]">
                    <td className="px-4 py-3">{it.name}{it.description ? <div className="text-xs text-[#8B7355]">{it.description}</div> : null}</td>
                    <td className="px-4 py-3 text-right">{it.quantity} {it.unit}</td>
                    <td className="px-4 py-3 text-right">{inr(it.unit_price)}</td>
                    <td className="px-4 py-3 text-right">{it.gst_rate}%</td>
                    <td className="px-4 py-3 text-right font-medium">{inr(net + gst)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-full max-w-sm space-y-1.5 text-sm bg-white border border-[#EAE5D9] rounded-lg p-5">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(quote.subtotal)}</span></div>
            {quote.is_interstate ? (
              <div className="flex justify-between"><span>IGST</span><span>{inr(quote.igst)}</span></div>
            ) : (
              <>
                <div className="flex justify-between"><span>CGST</span><span>{inr(quote.cgst)}</span></div>
                <div className="flex justify-between"><span>SGST</span><span>{inr(quote.sgst)}</span></div>
              </>
            )}
            <div className="flex justify-between border-t border-[#EAE5D9] pt-2 font-[Outfit] font-semibold text-lg text-[#C05621]"><span>Grand Total</span><span>{inr(quote.grand_total)}</span></div>
            {quote.doc_type === "invoice" && (
              <>
                <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{inr(quote.amount_paid || 0)}</span></div>
                <div className="flex justify-between text-red-700 font-semibold"><span>Amount Due</span><span>{inr(remaining)}</span></div>
              </>
            )}
          </div>
        </div>

        {quote.notes && (
          <div className="bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-4">
            <div className="uc-label !text-[#7B341E] mb-1">Notes</div>
            <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* Actions */}
        {!["accepted", "rejected"].includes(quote.public_action) && quote.doc_type !== "invoice" && (
          <div className="flex gap-3">
            <Button onClick={() => doAction("accept")} data-testid="public-accept-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"><CheckCircle2 className="w-4 h-4 mr-2" /> Accept Quotation</Button>
            <Button onClick={() => doAction("reject")} data-testid="public-reject-btn" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 flex-1"><XCircle className="w-4 h-4 mr-2" /> Reject</Button>
          </div>
        )}

        {remaining > 0 && (
          <Button onClick={payNow} disabled={payLoading} data-testid="public-pay-btn" className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white h-12 text-base">
            {payLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
            Pay {inr(remaining)} online now
          </Button>
        )}

        <div className="text-center text-xs text-[#8B7355] pt-6 border-t border-[#EAE5D9]">
          Powered by Bakemill ERP · Secure payment by Stripe · Test mode
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
